import React from 'react';
import { ColumnHelper, createColumnHelper } from '@tanstack/react-table';
import { useSelector } from 'react-redux';
import {
  type NetworkSiteTableData,
  columns as networkSiteTableColumns,
} from '../NetworkSiteTableColumns';
import { SELECT_WIDTH } from './constants';
import { SelectCell } from './SelectCell';
import { getStickyColumnStyle } from './usePtpColumns';
import {
  type SubscriberSiteTableData,
  columns as subscriberSiteTableColumns,
} from '../SubscriberSiteTableColumns';
import { buildCustomFieldColumns } from '../CustomFieldColumns';
import { useCustomFieldDefinitions } from 'src/hooks/useCustomFields';

const EMPTY_FIELDS: any[] = [];

// createColumnHelper() carries no per-render state, so each site kind's helper
// and column set live at module scope: a fresh helper per render would hand the
// `columns` memo below a new dependency every time, so nothing downstream of it
// (see useLpTable) could rely on a stable column list.
const SITE_TABLE_COLUMNS = {
  network_sites: {
    columnHelper: createColumnHelper<NetworkSiteTableData>() as ColumnHelper<any>,
    tableColumns: networkSiteTableColumns,
  },
  subscriber_sites: {
    columnHelper: createColumnHelper<SubscriberSiteTableData>() as ColumnHelper<any>,
    tableColumns: subscriberSiteTableColumns,
  },
};

function makeSiteColumns<T>(
  columnHelper: ColumnHelper<T>,
  tableColumns: any,
  prefs: any,
  customColumns: any[] = []
) {
  return [
    columnHelper.display({
      id: 'select',
      header: ({ table }) => <SelectCell kind="header" table={table} />,
      cell: ({ row, table }) => <SelectCell kind="row" row={row} table={table} />,
      enableResizing: false,
      enableHiding: false,
      meta: {
        tdStyle: getStickyColumnStyle(SELECT_WIDTH, 0),
      },
    }),
    // TODO probably better to do this with mutation and findIndex
    // since we only want to modify the name column
    ...tableColumns.map((column) => ({
      ...column,
      meta: {
        ...column.meta,
        prefs,
        tdStyle:
          column.id === 'name'
            ? getStickyColumnStyle(null, SELECT_WIDTH)
            : column.meta?.tdStyle,
      },
      enableGrouping: column.id === 'name',
    })),
    ...customColumns,
  ];
}

export function useSiteColumns(
  groupKind: 'network_sites' | 'subscriber_sites'
) {
  const prefs = useSelector((state: any) => state.mainFrame.prefs);
  const projectId = useSelector((state: any) => state.mainFrame.projectId);
  const { data: allDefs } = useCustomFieldDefinitions(projectId);
  const category =
    groupKind === 'network_sites' ? 'network_site' : 'subscriber_site';
  const customFieldDefs = allDefs?.[category] ?? EMPTY_FIELDS;

  const { columnHelper, tableColumns } = SITE_TABLE_COLUMNS[groupKind];

  const customColumns = React.useMemo(
    () => buildCustomFieldColumns(customFieldDefs, { choicesKind: 'site' }),
    [customFieldDefs]
  );

  const columns = React.useMemo(
    () => makeSiteColumns(columnHelper, tableColumns, prefs, customColumns),
    [prefs, columnHelper, tableColumns, customColumns]
  );

  return { columns };
}
